import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { CalendarDays, Clock, Target, Flame, CheckCircle2, Trophy } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayNarrator } from '../../../mascot';
import { getModuleIcon, MODULES, getModuleColors } from '../../../content';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { MS_PER_DAY } from '../../../config/constants';
import { getWeeklyActivityForLearner, getRecentSessionsForLearner } from '../../../utils/activityHelpers';
import { DataFreshness } from '../../shared/DataFreshness';
import { getChildLastActivity } from '../shared/childActivity';
interface ActivityPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  setViewingLearner: (id: string) => void;
}
export function ActivityPage({
  linkedLearners,
  selectedChild,
  childData,
  setViewingLearner
}: ActivityPageProps) {
  const {
    weeklyActivity,
    recentSessions,
    calendarWeeks,
    streakStats,
    weeklyStats
  } = useMemo(() => {
    if (!selectedChild || !childData) {
      return {
        weeklyActivity: [],
        recentSessions: [],
        calendarWeeks: [],
        streakStats: null,
        weeklyStats: {
          questions: 0,
          accuracy: 0,
          days: 0
        }
      };
    }
    const questionStates = childData.questionStates || {};
    // 1. Weekly Activity (current week)
    const weekly = getWeeklyActivityForLearner(questionStates);
    // 2. Recent Sessions
    const sessions = getRecentSessionsForLearner(questionStates, 7);
    // 3. Streak Stats — W4-5: slimmed from 4 cards to 2 (audit F-STAT-4).
    // Removed totalDailyGoalsReached + consecutiveFullWeeks: gamification
    // reporting with low parent action-value, mirrors the removed Prestaties-tab.
    const streakStats = {
      currentWeekDays: childData.dailyStreak?.currentWeekDays || 0,
      longestTotalStreak: childData.streak?.longestTotalStreak || 0
    };
    // 4. Weekly Summary Stats
    let questionsThisWeek = 0;
    let daysPracticedThisWeek = 0;
    weekly.forEach(day => {
      questionsThisWeek += day.questionCount;
      if (day.practiced) daysPracticedThisWeek++;
    });
    // Calculate accuracy for this week from recent sessions
    const now = Date.now();
    const oneWeekAgo = now - 7 * MS_PER_DAY;
    const sessionsThisWeek = sessions.filter(s => s.date >= oneWeekAgo);
    let totalQs = 0;
    let totalCorrect = 0;
    sessionsThisWeek.forEach(s => {
      totalQs += s.questionCount;
      totalCorrect += s.correctCount;
    });
    const accuracyThisWeek = totalQs > 0 ? Math.round(totalCorrect / totalQs * 100) : 0;
    // 5. 4-Week Calendar Grid
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const currentMonday = new Date(today);
    currentMonday.setDate(currentMonday.getDate() + mondayOffset);
    const weeks = [];
    for (let w = 3; w >= 0; w--) {
      const weekMonday = new Date(currentMonday);
      weekMonday.setDate(weekMonday.getDate() - w * 7);
      const weekMondayTs = weekMonday.getTime();
      const days = [];
      for (let d = 0; d < 7; d++) {
        const dayTs = weekMondayTs + d * MS_PER_DAY;
        const dayDate = new Date(dayTs);
        const isToday = dayTs === today.getTime();
        const isFuture = dayTs > today.getTime();
        // Count questions for this specific day
        let qCount = 0;
        for (const state of Object.values(questionStates)) {
          if (!state.history) continue;
          for (const attempt of state.history) {
            const attemptDate = new Date(attempt.timestamp);
            attemptDate.setHours(0, 0, 0, 0);
            if (attemptDate.getTime() === dayTs) {
              qCount++;
            }
          }
        }
        days.push({
          date: dayDate,
          isToday,
          isFuture,
          questionCount: qCount,
          practiced: qCount > 0
        });
      }
      // Label: e.g., "12 - 18 mrt"
      const startStr = `${weekMonday.getDate()} ${weekMonday.toLocaleString('nl-NL', {
        month: 'short'
      })}`;
      const endDay = new Date(weekMondayTs + 6 * MS_PER_DAY);
      const endStr = `${endDay.getDate()} ${endDay.toLocaleString('nl-NL', {
        month: 'short'
      })}`;
      weeks.push({
        label: w === 0 ? 'Deze week' : w === 1 ? 'Vorige week' : `${startStr} - ${endStr}`,
        days
      });
    }
    return {
      weeklyActivity: weekly,
      recentSessions: sessions,
      calendarWeeks: weeks,
      streakStats,
      weeklyStats: {
        questions: questionsThisWeek,
        accuracy: accuracyThisWeek,
        days: daysPracticedThisWeek
      }
    };
  }, [selectedChild, childData]);
  if (!selectedChild || !childData) {
    return <PageLayout title="Activiteit" description="Bekijk de oefenhistorie" data-id="element-7985" data-mp="2264ayaqnmax0">
        <div className="flex flex-col items-center justify-center py-20 text-center space-y-4" data-id="element-7986" data-mp="2241a0vnyqxdi">
          <DjayNarrator size="lg" emotion="thinking" data-id="element-7987" data-mp="1y757yvohgo87" />
          <p className="text-slate-500 font-medium" data-id="element-7988" data-mp="2c3ifpd37tb9h">
            Selecteer een kind om de activiteit te bekijken.
          </p>
        </div>
      </PageLayout>;
  }
  return <PageLayout title="Activiteit" description={`Bekijk wanneer en hoe ${selectedChild.displayName} oefent`} actions={<DataFreshness updatedAt={getChildLastActivity(childData)} data-id="element-7990" data-mp="2gwl8hj064gom" />} data-id="element-7989" data-mp="2luv3ntlw7ha8">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-7991" data-mp="2uu9voxhpjzwu">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-7992" data-mp="2usoz72bu26wc" />

        {/* Weekly Summary Strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-7993" data-mp="1ngpksb8zf8tc">
          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-7994" data-mp="1siewlt413qih">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0" data-id="element-7995" data-mp="1wife5mmfchtr">
              <CalendarDays className="w-5 h-5 text-emerald-500" data-id="element-7996" data-mp="21bic06y8idfr" />
            </div>
            <div data-id="element-7997" data-mp="2688mmer9sgts">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-7998" data-mp="268s7a38b1jbi">
                Dagen geoefend
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-7999" data-mp="2g7hgz8q1rxgo">
                {weeklyStats.days}{' '}
                <span className="text-sm font-bold text-slate-400" data-id="element-8000" data-mp="2k9bbez9ng58e">
                  / 7 deze week
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-8001" data-mp="pusb93ckydtj">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-8002" data-mp="pwlfkbzq0lyd">
              <Target className="w-5 h-5 text-blue-500" data-id="element-8003" data-mp="dksvbtjww687" />
            </div>
            <div data-id="element-8004" data-mp="dig4eqw6elj0">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-8005" data-mp="8lfzijb2both">
                Vragen beantwoord
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-8006" data-mp="4l5qexrz4iae">
                {weeklyStats.questions}{' '}
                <span className="text-sm font-bold text-slate-400" data-id="element-8007" data-mp="119lltahrllvf">
                  deze week
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-8008" data-mp="s8dwvj151xe0">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-8009" data-mp="nb5cdbsiph9l">
              <CheckCircle2 className="w-5 h-5 text-purple-500" data-id="element-8010" data-mp="3d0pkfz0gy4qi" />
            </div>
            <div data-id="element-8011" data-mp="380bj0ztdht1g">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-8012" data-mp="37zs1hk7mx6ny">
                Nauwkeurigheid
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-8013" data-mp="1bzsx7e9lfi70">
                {weeklyStats.accuracy}%{' '}
                <span className="text-sm font-bold text-slate-400" data-id="element-8014" data-mp="11zil5wzaogtf">
                  deze week
                </span>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-8015" data-mp="1706kd5xog3yq">
          {/* Left Column: Calendar & Streaks */}
          <div className="lg:col-span-2 space-y-8" data-id="element-8016" data-mp="1zmrci8lz8k3c">
            {/* 4-Week Calendar */}
            <motion.section initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-8017" data-mp="1ky4m9buta7xc">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-8018" data-mp="2idijx4mem0f3">
                <CalendarDays className="w-4 h-4 text-emerald-500" data-id="element-8019" data-mp="6y03ejoc917n" />
                Oefenkalender
              </h2>
              <Card className="p-6 bg-white border-slate-200 shadow-sm" data-id="element-8020" data-mp="1ydu2lbms544">
                <div className="flex justify-between items-end mb-6" data-id="element-8021" data-mp="fqljtt4hyvtb">
                  <div className="flex gap-4 text-xs font-medium text-slate-500" data-id="element-8022" data-mp="fsoamfdh4sz3">
                    <div className="flex items-center gap-1.5" data-id="element-8023" data-mp="bq1o8tzko09a">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-8024" data-mp="3k637w90852oc" />{' '}
                      Geoefend
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-8025" data-mp="3f81pzrejb9eg">
                      <div className="w-3 h-3 rounded-full bg-slate-100" data-id="element-8026" data-mp="3t6symyamo2v7" /> Niet
                      geoefend
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-8027" data-mp="18ezimb3etw5x">
                  {/* Day headers — F-ACT-2: responsive label column (44px mobile → 80px sm+) */}
                  <div className="grid grid-cols-[44px_repeat(7,1fr)] sm:grid-cols-[80px_repeat(7,1fr)] gap-1 sm:gap-2 mb-2" data-id="element-8028" data-mp="13g67gpblih8x">
                    <div data-id="element-8029" data-mp="pobxf2bx7336"></div>
                    {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => <div key={day} className="text-center text-xs font-bold text-slate-400" data-id="element-8030" data-mp="kxderw3glswr">
                        {day}
                      </div>)}
                  </div>

                  {/* Weeks */}
                  {calendarWeeks.map((week, i) => <div key={i} className="grid grid-cols-[44px_repeat(7,1fr)] sm:grid-cols-[80px_repeat(7,1fr)] gap-1 sm:gap-2 items-center" data-id="element-8031" data-mp="6wj416sxvfux" data-mpi={i}>
                      <div className="text-[10px] sm:text-xs font-medium text-slate-500 text-right pr-1 sm:pr-4 truncate" data-id="element-8032" data-mp="3u4mu6oq629iz">
                        {week.label}
                      </div>
                      {week.days.map((day, j) => {
                    const dateLabel = day.date.toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'short'
                    });
                    const ariaLabel = day.isFuture ? `${dateLabel}: in de toekomst` : day.practiced ? `${dateLabel}: ${day.questionCount} vragen geoefend${day.isToday ? ' (vandaag)' : ''}` : `${dateLabel}: niet geoefend${day.isToday ? ' (vandaag)' : ''}`;
                    return <div key={j} className="flex justify-center" data-id="element-8033" data-mp="30avymhcxy7h2" data-mpi={j}>
                            <div role="img" aria-label={ariaLabel} className={`rounded-full transition-all ${day.isFuture ? 'w-6 h-6 bg-transparent border border-slate-100' : day.practiced ? day.isToday ? 'w-8 h-8 bg-emerald-500 shadow-md shadow-emerald-200 ring-2 ring-emerald-300 ring-offset-1' : 'w-6 h-6 bg-emerald-400' : day.isToday ? 'w-8 h-8 bg-slate-100 border-2 border-emerald-200 ring-2 ring-emerald-200 ring-offset-1' : 'w-6 h-6 bg-slate-100'} flex items-center justify-center`} title={day.isFuture ? '' : ariaLabel} data-id="element-8034" data-mp="30cyzjwfmtl0k">
                              {day.isToday && day.practiced && <CheckCircle2 className="w-4 h-4 text-white" data-id="element-8035" data-mp="1xx6kj7dyjk07" />}
                            </div>
                          </div>;
                  })}
                    </div>)}
                </div>
              </Card>
            </motion.section>

            {/* Streak Stats */}
            <motion.section initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} data-id="element-8036" data-mp="1f5drjcyw1ysh">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-8037" data-mp="3qkcmm783hrh5">
                <Flame className="w-4 h-4 text-orange-500" data-id="element-8038" data-mp="3cu0s61o1fgmu" />
                Reeksen
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-8039" data-mp="3gu1k8p427qcd">
                <Card className="p-6 bg-white border-slate-200 shadow-sm flex flex-col justify-center items-center text-center" data-id="element-8040" data-mp="3gsgl0m6svj86">
                  <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mb-3" data-id="element-8041" data-mp="dem8njg198p4">
                    <Flame className="w-7 h-7 text-orange-500" data-id="element-8042" data-mp="3vkigzv5r1s2x" />
                  </div>
                  <div className="text-4xl font-black text-slate-800 mb-1" data-id="element-8043" data-mp="4cwi0ftz6dse">
                    {streakStats?.currentWeekDays || 0}
                  </div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-8044" data-mp="2k6n28nirblc8">
                    Dagen op rij
                  </div>
                </Card>

                <Card className="p-6 bg-white border-slate-200 shadow-sm flex flex-col justify-center items-center text-center" data-id="element-8045" data-mp="34e2241islei9">
                  <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3" data-id="element-8046" data-mp="2zkh449h78kj2">
                    <Trophy className="w-7 h-7 text-amber-500" data-id="element-8047" data-mp="2ul4ifs22dji2" />
                  </div>
                  <div className="text-4xl font-black text-slate-800 mb-1" data-id="element-8048" data-mp="2qjsoxbg2ke4l">
                    {streakStats?.longestTotalStreak || 0}
                  </div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-8049" data-mp="3ie4i1i7jhxuv">
                    Langste reeks ooit
                  </div>
                </Card>
              </div>
            </motion.section>
          </div>

          {/* Right Column: Recent Sessions */}
          <div className="space-y-8" data-id="element-8050" data-mp="22wkqndh265c2">
            <motion.section initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} data-id="element-8051" data-mp="22xm2hk5wevuu">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-8052" data-mp="ic5xcl72mk1n">
                <Clock className="w-4 h-4 text-blue-500" data-id="element-8053" data-mp="r6kaah44pihl" />
                Recente Sessies
              </h2>

              <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-8054" data-mp="w5x81wwjg9yy">
                {recentSessions.length > 0 ? <div className="divide-y divide-slate-100" data-id="element-8055" data-mp="113p14u8x624q">
                    {recentSessions.map((session, i) => {
                  const IconComp = getModuleIcon(session.moduleIcon);
                  const moduleData = session.moduleId ? MODULES.find(m => m.id === session.moduleId) : null;
                  const colors = getModuleColors(moduleData?.color || 'text-slate-500');
                  const date = new Date(session.date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  const dateStr = isToday ? 'Vandaag' : date.toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'short'
                  });
                  const timeStr = date.toLocaleTimeString('nl-NL', {
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  return <div key={i} className="p-4 hover:bg-slate-50 transition-colors" data-id="element-8056" data-mp="1bst649u8a78y" data-mpi={i}>
                          <div className="flex items-center justify-between mb-3" data-id="element-8057" data-mp="1ks7u92mxa5qa">
                            <div className="text-xs font-bold text-slate-400" data-id="element-8058" data-mp="1ftmsgys0vdzq">
                              {dateStr} om {timeStr}
                            </div>
                            <div className={`text-xs font-bold px-2 py-0.5 rounded-full ${session.accuracy >= 80 ? 'bg-emerald-100 text-emerald-700' : session.accuracy >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-8059" data-mp="24d837e3k4red">
                              {session.accuracy}% goed
                            </div>
                          </div>

                          <div className="flex items-center gap-3" data-id="element-8060" data-mp="1levq5h9iyxdi">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.light} ${colors.text}`} data-id="element-8061" data-mp="16ppwe8tghzd0">
                              <IconComp className="w-5 h-5" data-id="element-8062" data-mp="xp9uh3sd30a9" />
                            </div>
                            <div className="min-w-0" data-id="element-8063" data-mp="12o4jingx9l2t">
                              <div className="font-bold text-sm text-slate-900 truncate" data-id="element-8064" data-mp="12nl53gqhdetj">
                                {session.moduleTitle}
                              </div>
                              <div className="text-xs font-medium text-slate-500" data-id="element-8065" data-mp="1d82s3ka1plkt">
                                {session.questionCount} vragen geoefend
                              </div>
                            </div>
                          </div>
                        </div>;
                })}
                  </div> : <div className="p-8 text-center" data-id="element-8066" data-mp="ugtcct9om0ks">
                    <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-8067" data-mp="uf8jr96xgrpk">
                      <Clock className="w-6 h-6 text-slate-300" data-id="element-8068" data-mp="pf40xcijrkw6" />
                    </div>
                    <p className="text-sm font-medium text-slate-500" data-id="element-8069" data-mp="kgaprqqqg5z6">
                      Nog geen recente sessies.
                    </p>
                  </div>}
              </Card>
            </motion.section>
          </div>
        </div>
      </div>
    </PageLayout>;
}